import React, { useEffect, useRef, useState } from "react";
import ReportCover from "@/components/marketing/ReportCover";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowUpRight, ArrowRight, CheckCircle2, FileBarChart2, Loader2,
  ShieldCheck, BarChart3, Percent, Calculator, Download, Linkedin, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const INSIDE = [
  { Icon: BarChart3, t: "Sector CPL benchmarks", d: "Live marketplace base prices for exclusive leads across all ten UK finance verticals." },
  { Icon: Percent, t: "Close-rate ranges", d: "Typical buyer-reported operating bands per sector — know what good looks like." },
  { Icon: Calculator, t: "Implied CPA economics", d: "What each lead price actually costs per won customer, at both ends of the band." },
  { Icon: ShieldCheck, t: "How to apply it", d: "Break-even maths, exclusivity effects, and how to judge value sector by sector." },
];

export default function BenchmarkReport() {
  return (
    <div className="bg-white">
      {/* ── HERO + GATE ─────────────────────────────────── */}
      <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/40">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="absolute -top-40 -left-24 w-[600px] h-[600px] rounded-full bg-[#3d7bff]/[0.06] blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-28">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                <FileBarChart2 className="size-3.5" />
                Connexis Intelligence · Free report
              </div>
              <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[60px] tracking-[-0.045em] font-semibold text-slate-900 leading-[1.04]">
                The 2026 UK Financial Services{" "}
                <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">
                  CPL Benchmark Report.
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-[17px] text-slate-600 leading-relaxed">
                What should a mortgage lead cost? What close rate makes a £145 commercial finance
                enquiry cheap? Ten sectors of live marketplace pricing, translated into the economics
                that actually matter.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-[#0055FF]" /> 5-page designed PDF</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-[#0055FF]" /> Live marketplace data</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="size-3.5 text-[#0055FF]" /> No sales call required</span>
              </div>
              {/* mini report cover mock */}
              <div className="mt-10 hidden sm:flex items-center gap-5">
                <CoverMock />
                <div className="text-[12px] text-slate-500 leading-relaxed max-w-[220px]">
                  Benchmarks, tables and charts across ten sectors — from £14 payday CPLs to £145 commercial finance.
                </div>
              </div>
              <ShareRow />
            </div>
            <GateForm />
          </div>
        </div>
      </section>

      {/* ── WHAT'S INSIDE ───────────────────────────────── */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0055FF]">What's inside</p>
            <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">Numbers you can negotiate with.</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INSIDE.map((x, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#0055FF]/40 hover:shadow-soft transition-all" data-testid={`report-inside-${i}`}>
                <span className="inline-grid place-items-center size-10 rounded-lg bg-slate-900 text-white">
                  <x.Icon className="size-5" strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 text-[16px] font-medium tracking-tight text-slate-900">{x.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────── */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-900 text-white p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h3 className="text-3xl sm:text-4xl tracking-[-0.03em] font-medium">Rather see your own numbers?</h3>
              <p className="mt-3 text-slate-400 max-w-lg">The live ROI calculator models your sector, budget and close rate against real pricing — no download needed.</p>
            </div>
            <Link to="/buy-leads#calculator">
              <Button data-testid="report-roi-cta" className="rounded-full px-6 h-12 bg-white text-slate-900 hover:bg-slate-100 font-semibold">
                Open the calculator <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ShareRow() {
  const shareUrl = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/api/r/benchmark`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — rich preview included");
    } catch (_) {
      toast.error("Could not copy link");
    }
  };
  return (
    <div className="mt-7 flex flex-wrap items-center gap-3 text-[13px]">
      <span className="text-slate-400">Know someone who'd want this?</span>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
        target="_blank"
        rel="noreferrer"
        data-testid="report-share-linkedin"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 font-medium text-slate-700 hover:border-[#0055FF]/40 hover:text-[#0055FF] transition-colors"
      >
        <Linkedin className="size-3.5" /> Share on LinkedIn
      </a>
      <button
        onClick={copy}
        data-testid="report-share-copy"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 font-medium text-slate-700 hover:border-[#0055FF]/40 hover:text-[#0055FF] transition-colors"
      >
        <Link2 className="size-3.5" /> Copy link
      </button>
    </div>
  );
}

function CoverMock() {
  return <ReportCover tilt={-3} testId="report-cover-mock" />;
}

function GateForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const successRef = useRef(null);

  useEffect(() => {
    if (done) {
      setTimeout(() => successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    }
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and work email");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/reports/benchmark", form, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Connexis-CPL-Benchmark-Report-2026.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      localStorage.setItem("connexis_exit_capture_done", "1");
      setDone(true);
    } catch (_) {
      toast.error("Could not generate your report — please try again");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div ref={successRef} className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-[0_24px_60px_-30px_rgba(15,23,42,0.3)]" data-testid="report-success">
        <span className="mx-auto grid place-items-center size-12 rounded-full bg-emerald-50 text-emerald-600">
          <Download className="size-6" />
        </span>
        <h3 className="mt-5 text-xl font-medium text-slate-900">Your download has started.</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          A copy is also on its way to your inbox. When you're ready to test the benchmarks against your own desk:
        </p>
        <Link to="/buy-leads#calculator">
          <Button className="mt-6 rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="report-success-cta">
            Model your sector <ArrowUpRight className="size-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.3)] space-y-5" data-testid="report-gate-form">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">Instant download</div>
        <h3 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-900">Get the report — free.</h3>
        <p className="mt-1 text-[13px] text-slate-500">PDF downloads instantly. A copy lands in your inbox too.</p>
      </div>
      <div>
        <Label htmlFor="rep-name" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</Label>
        <Input id="rep-name" data-testid="report-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
      </div>
      <div>
        <Label htmlFor="rep-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Work email *</Label>
        <Input id="rep-email" data-testid="report-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.co.uk" />
      </div>
      <div>
        <Label htmlFor="rep-company" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Company</Label>
        <Input id="rep-company" data-testid="report-company" className="mt-1.5" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company Ltd" />
      </div>
      <Button type="submit" disabled={sending} data-testid="report-submit" className="w-full rounded-full h-11 bg-[#0055FF] hover:bg-[#0044CC]">
        {sending ? <Loader2 className="size-4 animate-spin" /> : <>Download the report <ArrowUpRight className="size-4" /></>}
      </Button>
      <p className="text-[11px] text-slate-400 text-center">No spam — one useful report and nothing else unless you ask.</p>
    </form>
  );
}
